import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { RegisterUserModel } from 'src/app/models/registeruser.model';
import { UpdateUserModel } from 'src/app/models/updateuser.model';
import { LoginUserModel } from 'src/app/models/loginuser.model';
import { StoragePermissionService } from '../storagePermission/storage-permission.service';
import { Socialtype } from '../../enums/socialtype.enum';
import { RecoveryPasswordModel } from 'src/app/models/recoverypassword.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LoginResponseModel } from '../../models/loginresponse.model';
import { WindorefService } from '../window/windoref.service';
import { RecoveryPasswordConfirmModel } from 'src/app/models/recoverypasswordconfirm.model';
import { GlobalsService } from '../globals/globals.service';
import { Language } from 'src/app/tools/language/language';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  static multisite = environment.multisite;
  public isUserLoggedBehavior = new BehaviorSubject(false);
  isUserLogged = this.isUserLoggedBehavior.asObservable();
  apiUrl = environment.rapi;
  headers: any;
  system: string;

  constructor(
    private http: HttpClient,
    private _storageService: StoragePermissionService,
    private router: Router,
    private _snackBar: MatSnackBar,
    private winRef: WindorefService,
    private _global: GlobalsService
  ) {
    if (this._storageService.isAuthenticatedByToken()) {
      this.isUserLoggedBehavior.next(true);
    } else {
      this.isUserLoggedBehavior.next(false);
    }
    this.system = this._global.getSystem().site;
  }

  getToken(): string {
    return this._storageService.getToken();
  }

  getExpireToken(): number {
    return this._storageService.getExpireToken();
  }

  getHeader() {
    this.headers = new Headers({
      Authorization: `Bearer ${this.getToken()}`,
      'content-type': 'multipart/form-data; boundary=---011000010111000001101001'
    });
    return this.headers;
  }

  loginUser(data: LoginUserModel): Observable<any> {
    const httpOptions = {
      headers: this.getHeader()
    };
    let loginData = new FormData();
    loginData.append('username', data.username);
    loginData.append('password', data.password);
    return this.http.post<any>(`${this.apiUrl}/api/oauth/token`, loginData, httpOptions);
  }

  registerUser(data: RegisterUserModel): Observable<any> {
    localStorage.setItem('spinnerProgress', String(5));
    const source = environment.source;
    const body = {
      ...data,
      source
    };
    const httpOptions = {
      headers: this.getHeader()
    };
    const endpointReturn = this.http.post(this.apiUrl + '/api/users', body, httpOptions);
    localStorage.setItem('spinnerProgress', String(15));
    return endpointReturn;
  }

  registerUserHalfUser(data: RegisterUserModel): Observable<any> {
    const source = environment.source;
    const body = {
      ...data,
      source,
      mode_register: 0
    };
    const httpOptions = {
      headers: this.getHeader()
    };
    return this.http.post(this.apiUrl + '/api/half-users', body, httpOptions);
  }

  singIn(username: string, token: string, expires_in: string, cartId: number | boolean = false): Observable<any> {
    localStorage.setItem('spinnerProgress', String(25));
    let { type, pcode } = environment;
    const api = this._storageService.getDomainToRedirect();
    const clientEnv = this._storageService.getEnvironment();
    let body: {} = {
      access_token: token,
      expires_in: expires_in,
      username: username,
      environment: clientEnv,
      addFunds: false
    };

    if (cartId !== false) body = { ...body, cartId: cartId };

    if (type === 'casino-deposit') body = { ...body, depositCode: pcode[this.system], addFunds: true };

    const httpOptions = {
      headers: this.getHeader()
    };
    const endpointReturn = this.http.post(this.apiUrl + '/api/tools/hash-landing', body, httpOptions);
    localStorage.setItem('spinnerProgress', String(30));
    return endpointReturn;
  }

  singInCasino(username: string, token: string, expires_in: string, gameID: number | boolean = false): Observable<any> {
    localStorage.setItem('spinnerProgress', String(25));
    const api = this._storageService.getDomainToRedirect();
    const clientEnv = this._storageService.getEnvironment();
    let body: any;

    body = {
      access_token: token,
      expires_in: expires_in,
      username: username,
      gameId: gameID,
      environment: clientEnv
    };
    const httpOptions = {
      headers: this.getHeader()
    };
    const endpointReturn = this.http.post(this.apiUrl + '/api/tools/hash-landing', body, httpOptions);
    localStorage.setItem('spinnerProgress', String(30));
    return endpointReturn;
  }

  updateUser(data: UpdateUserModel): Observable<any> {
    const httpOptions = {
      headers: {
        authorization: `Bearer ${this.getToken()}`,
        'Content-Type': 'application/json; charset=utf-8',
        accept: ' application/json'
      }
    };
    return this.http.post(`${this.apiUrl}/api/users`, data, httpOptions);
  }

  getInfoStoreUsers(): Observable<any> {
    const httpOptions = {
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        accept: ' application/json',
        'Content-Language': Language.currentLanguage.lenid.toString()
      }
    };
    return this.http.get(`${this.apiUrl}/api/users/create`, httpOptions);
  }

  logOutUser(): Observable<any> {
    this.isUserLoggedBehavior.next(false);
    const httpOptions = {
      headers: {
        'Content-Type': 'multipart/form-data; boundary=---011000010111000001101001',
        Accept: 'application/json',
        Authorization: `Bearer ${this.getToken()}`
      }
    };
    this.router.navigate(['/home']);
    return this.http.get(`${this.apiUrl}/api/users/logout`, httpOptions);
  }

  recoveryAccount(data: RecoveryPasswordModel): Observable<any> {
    const httpOptions = {
      headers: {
        accept: 'application/json',
        'Content-Type': 'application/json; charset=utf-8'
      }
    };
    return this.http.post(`${this.apiUrl}/api/password/recovery`, data, httpOptions);
  }

  recoveryAccountConfirm(data: RecoveryPasswordConfirmModel): Observable<any> {
    const httpOptions = {
      headers: {
        accept: 'application/json',
        'Content-Type': 'application/json; charset=utf-8'
      }
    };
    return this.http.post(`${this.apiUrl}/api/password/recovery/confirm`, data, httpOptions);
  }

  /**
   * get uri to redirect to social login
   * @param socialType type social to do login
   */
  socialUri(socialType: Socialtype, router: Router) {
    this._storageService.setStorage('socialLoginType', socialType);
    this._storageService.setStorage('uri', router.url);
    return this.http.get(this.apiUrl + '/auth/' + socialType, { responseType: 'text' });
  }

  /**
   * When the login is confirmed this method verify the token and start sesión
   */
  confirmSocialLogin(router: Router) {
    const urlParams = new URLSearchParams(this.winRef.getNativeWindow().location.search);
    const code = urlParams.get('code');
    const socialType = this._storageService.getStorage('socialLoginType');
    if (code && socialType) {
      let parameters = '/callback?';
      urlParams.forEach((value, key) => {
        if (parameters === '/callback?') {
          parameters = parameters + key + '=' + value;
        } else {
          parameters = parameters + '&' + key + '=' + value;
        }
      });
      this.http.get(this.apiUrl + '/auth/' + socialType + parameters).subscribe(
        (result) => {
          const response = JSON.parse(JSON.stringify(result));
          if (response.register) {
            this._storageService.setStorage('register', response.register);
          } else {
            this._storageService.setStorage('register', undefined);
          }
          let tokenResponse: LoginResponseModel = {
            access_token: response.user.token,
            expires_in: response.user.expiresIn,
            refresh_token: response.user.refreshToken,
            token_type: response.token_type
          };

          this._storageService.setToken(tokenResponse);
          if (socialType === Socialtype.google) {
            this._storageService.setStorageProfile(JSON.stringify(response.user.name));
          } else if (socialType === Socialtype.facebook) {
            this._storageService.setStorageProfile(JSON.stringify(response.user.name));
          }
          this.responseSocialLogin(router);
        },
        (err) => {
          this.responseSocialLogin(router);
        }
      );
    }
  }

  /**
   * When the user confirm the login with any social network from a mobile device this method is called
   */
  responseSocialLogin(router: Router) {
    if (this._storageService.getProfile()) {
      this.isUserLoggedBehavior.next(true); // update global menu
      if (this._storageService.getStorage('register') !== 'undefined' && this._storageService.getStorage('register')) {
        this.router.navigate(['my-account/personal-information']);
      } else {
        if (this._storageService.getStorage('uri') !== 'undefined' && this._storageService.getStorage('uri')) {
          const uri = this._storageService.getStorage('uri');
          const url = unescape(uri).split('?');
          if (url.length === 2) {
            router.navigate([url[0]], { queryParams: JSON.parse('{"' + decodeURI(url[1].replace(/&/g, '","').replace(/=/g, '":"')) + '"}') });
          } else {
            router.navigate([uri]);
          }
        } else {
          router.navigate(['home']);
        }
      }
    } else {
      this._snackBar.open('Error logging in, try again', 'OK', {
        duration: 2000
      });
    }
  }
}
